/**
 * @fileoverview Order shipping management component for upload and tracking operations.
 * Handles shipping label uploads, QR code scanning, PDF processing, and delivery confirmation.
 * Supports multiple file formats and OCR text recognition for automated data extraction.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

/* eslint-disable no-unused-vars */
/* eslint-disable eqeqeq */
import { Button, Grid, Stack, TextField, Divider, IconButton } from '@mui/material';
import React, { useState, useCallback, useRef } from 'react';
import { MultipleStop, RemoveCircleOutline, Upload, UploadFile } from '@mui/icons-material';
import MDialog from '../../../../components/controls/MDialog';
import { useDropzone } from 'react-dropzone';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import jsQR from 'jsqr';
import { useRecordContext } from 'react-admin';
import { formatDateTime } from '../../../../components/ex_dayjs';
import { STATUS_ENUM } from '../../../../utils/enum';
import Tesseract from 'tesseract.js';
import MCheckbox from '../../../../components/controls/MCheckbox';
import LoadingButton from '../../../../components/controls/LoadingButton';

interface IData {
    [key: string]: any;
}

/**
 * Markets that support the Transfer Complete button functionality.
 * @constant {string[]}
 */
const TRANSFER_COMPLETE_MARKETS = ['axs', 'exact'];

/**
 * Order shipping management component for handling shipping operations and customer info reveal.
 * Manages shipping validations, file uploads, and reveals customer information for Direct Transfer
 * and Flash orders after successful shipping button click.
 *
 * **URL-upload suppression rules for Direct Transfer (delivery_method == 5) + etix orders:**
 * - `Vivid`: URL-link upload is suppressed; mobile-QR path (`isMobileQR`) is used instead
 *   (`isEtixVVTransfer`).
 * - `GoTickets`: URL-link upload is suppressed; mobile-QR path (`isMobileQR`) is used instead
 *   (`isEtixGoTicketsTransfer`), same as Vivid.
 * - `TicketNetwork`: URL-link upload is suppressed and the shipping button is hidden entirely
 *   (`isEtixTNTransfer`; `showShippingBtn` remains false).
 * - All other vendors: URL-link upload (`is_Upload_Url`) applies as normal.
 *
 * @param {Object} props - Component properties
 * @param {Function} props.notify - Notification function
 * @param {Object} props.dataProvider - Data provider for API operations
 * @param {Object} props.vendor - Vendor configuration object containing `controller` ('Vivid',
 *   'TicketNetwork', 'GoTickets', etc.) used for vendor-specific upload gating
 * @param {Function} props.validate - Validation function
 * @param {Function} props.validComplete - Complete validation function
 * @param {boolean} props.openShipping - Flag to open shipping popup
 * @param {Function} props.closePopup - Function to close popup
 * @param {Function} props.setDlgState - Function to set dialog state
 * @param {number} props.cur_delivery_method - Current delivery method (5 = Direct Transfer)
 * @param {boolean} props.isUploadPOA - Flag for proof of acceptance upload
 * @param {Object} props.customer - Customer information for reveal functionality
 * @param {Function} props.revealCustomerInfo - Function to reveal customer info and create system note
 * @param {Function} props.saveOrder - Function to save order changes (for Transfer Complete)
 * @returns {JSX.Element} Shipping management interface
 */
const OrderShipping = (props: IData) => {
    const {
        // Data & Services
        dataProvider,
        customer,
        vendor,
        orderData,

        // Component State & Flags
        cur_delivery_method,
        isUploadPOA,

        // Action Handlers
        validate,
        validComplete,
        revealCustomerInfo,
        saveOrder,

        // Dialog/Popup Handlers
        openShipping,
        closePopup,
        setDlgState,
        setOrderData,

        // Utilities
        notify,
    } = props;
    const record = useRecordContext();
    const controller = vendor.controller;
    if (!record) return;

    const [shippingPopup, setShippingPopup] = useState<IData>({ isUploadUrl: false, isBothUpload: false, open: false, showShippingBtn: false, isUploadPOA: isUploadPOA || false, title: '' });
    const [previewPDF, setPreviewPDF] = useState<IData>({ width: '100%', height: '500px', blobURL: null, isMobileQR: false, filesNum: 0, selectedFiles: null, loadingFile: false });
    const [uploadBtn, setUploadBtn] = useState<IData>({ hide: false, unmatching: false, invalidText: false, showForceUpload: false, forceUpload: false });
    const [transferLink, setTransferLink] = useState<IData[]>([{ url: '' }, { url: '' }]);
    const [transferLoading, setTransferLoading] = useState(false);
    const fileInputRef = useRef<any>(null);

    //Use a React ref to always access the latest value
    const deliveryMethodRef = useRef(cur_delivery_method);
    React.useEffect(() => {
        let event_market = orderData.event_market || '';
        let deliveryMethod = cur_delivery_method ?? orderData.delivery_method_value;
        let isDirectTransfer = deliveryMethod == 5;
        let isWillCall = deliveryMethod == 6;
        let isHardTicket = deliveryMethod == 1;
        deliveryMethodRef.current = deliveryMethod;

        //Forced upload mobile QR for Vivid Seats ETIX DT orders
        let isEtixVVTransfer = controller === 'Vivid' && event_market.toLowerCase() === 'etix' && isDirectTransfer;
        // GoTickets DT etix order uses mobile-QR, same as Vivid
        let isEtixGoTicketsTransfer = controller === 'GoTickets' && event_market.toLowerCase() === 'etix' && isDirectTransfer;
        if ((deliveryMethod == 3 && controller === 'GoTickets') || isEtixVVTransfer || isEtixGoTicketsTransfer) {
            // GoTickets: Delivery = Mobile QR; Vivid/GoTickets ETIX DT = Mobile QR
            setPreviewPDF(prev => ({ ...prev, isMobileQR: true }));
        }

        // Suppress URL-link upload for TicketNetwork ETIX DT orders; showShippingBtn remains false
        let isEtixTNTransfer = controller === 'TicketNetwork' && event_market.toLowerCase() === 'etix' && isDirectTransfer;

        //Axs & Tdc & ticketweb & exact: upload proof for DT
        let is_Upload_Url = isDirectTransfer && !['axs', 'tdc', 'ticketweb', 'exact'].some(s => event_market.toLowerCase() === s) && !isEtixVVTransfer && !isEtixGoTicketsTransfer && !isEtixTNTransfer;
        // Exact: Delivery = Direct Transfer ? Upload ULRs
        let isBothUpload = (is_Upload_Url && event_market == 'paciolan') || (event_market.toLowerCase() === 'exact');
        let isShowUploadPopup = is_Upload_Url || (!isWillCall && !isHardTicket);

        let _title = isBothUpload ? 'Upload Proofs | URLs' : is_Upload_Url ? 'Upload URLs' : 'Upload Files';
        if (shippingPopup.isUploadPOA) _title = 'Upload Proof of Acceptance';
        // TN ETIX DT: suppress shipping button entirely (showShippingBtn stays false)
        if (isShowUploadPopup && !isEtixTNTransfer) {
            setShippingPopup(prev => ({ ...prev, isUploadUrl: is_Upload_Url, isBothUpload: isBothUpload, showShippingBtn: true, title: _title }));
        }

    }, [cur_delivery_method]);

    React.useEffect(() => {
        setShippingPopup(prev => ({ ...prev, open: openShipping }));
    }, [openShipping]);

    const closeShippingPopup = () => {
        setPreviewPDF(prev => ({ ...prev, blobURL: null, isMobileQR: false, filesNum: 0, selectedFiles: null, excludes: null, newBlob: null }));
        setUploadBtn(prev => ({ ...prev, hide: false, unmatching: false, invalidText: false, showForceUpload: false, forceUpload: false }));
        setTransferLink([{ url: '' }, { url: '' }]);
        setShippingPopup(prev => ({ ...prev, open: false }));
        closePopup();
    };
    //#region upload file
    const onDrop = useCallback(async (acceptedFiles: any[]) => {
        setPreviewPDF(prev => ({ ...prev, loadingFile: true }));
        setUploadBtn(prev => ({ ...prev, hide: true, showForceUpload: false }));
        await workWithFiles(acceptedFiles);
        setPreviewPDF(prev => ({ ...prev, selectedFiles: acceptedFiles, filesNum: acceptedFiles.length, loadingFile: false }));
    }, [previewPDF?.excludes, previewPDF?.isMobileQR]);

    async function workWithFiles (inputFiles: any) {
        let combinedFile = await combineFilesToPDF(inputFiles, getPageExcludes(previewPDF?.excludes));
        fileInputRef.current = combinedFile;
        let blobUrl = URL.createObjectURL(combinedFile) + '#zoom=FitWidth';
        setPreviewPDF(prev => ({ ...prev, blobURL: blobUrl }));
    }
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    // Determine the image embedding method based on the file type
    function getImageEmbedMethod (type: string) {
        return new Promise((resolve) => {
            const fileType = type.toLowerCase();
            if (fileType === 'image/png') {
                resolve((doc: any, bytes: any) => doc.embedPng(bytes));
            } else if (fileType === 'image/jpeg' || fileType === 'image/jpg') {
                resolve((doc: any, bytes: any) => doc.embedJpg(bytes));
            } else if (fileType.includes('image/')) {
                resolve(async (doc: any, bytes: any) => {
                    const blob = new Blob([bytes], { type: 'image/gif' });
                    const imgUrl = URL.createObjectURL(blob);

                    const img = new Image();
                    img.src = imgUrl;

                    await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                    });

                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);

                    // Convert canvas to PNG Blob
                    const pngBlob = await new Promise((resolve) =>
                        canvas.toBlob(resolve, 'image/png')
                    );

                    // Convert Blob to ArrayBuffer / Uint8Array
                    const arrayBuffer = await pngBlob.arrayBuffer();

                    return doc.embedPng(arrayBuffer);
                });
            }
        });
    }

    function getPageExcludes (range: any) {
        const _excludes: number[] = [];
        if (!range) return;

        const parts = range.split(',');
        for (let part of parts) {
            part = part.trim();
            if (part.includes('-')) {
                const [start, end] = part.split('-').map(Number);
                for (let i = start; i <= end; i++) _excludes.push(i);
            } else {
                _excludes.push(Number(part));
            }
        }

        return _excludes;
    }

    const combineFilesToPDF = async (filesInput: any, excludePages: number[] = []) => {
        const readerPromises: any[] = [], arrQR: any[] = [], arrText: any[] = [];

        // const files = Array.from(fileInput.files);
        filesInput.forEach((file: any) => {
            const reader = new FileReader();
            const promise = new Promise((resolve) => {
                reader.onload = function () {
                    const pdfBytes = new Uint8Array(reader.result);
                    resolve({
                        name: file.name,
                        type: file.type,
                        data: pdfBytes,
                        file: file
                    });
                };
            });

            reader.readAsArrayBuffer(file);
            readerPromises.push(promise);
        });
        //===========================================================
        // Wait for all reader promises to resolve
        const results = await Promise.all(readerPromises);

        // Create a merged pdf
        const mergedPdf = await PDFDocument.create();
        // Embed font in the modified PDF
        const font = await mergedPdf.embedFont(StandardFonts.Helvetica);

        try {

            // Combine files result into the merged PDF
            for (const result of results) {
                await combineFile(mergedPdf, result);
            }

            // Save the merged PDF
            const original = await mergedPdf.save();

            // Create a new modified PDF document
            const modifiedPDF = await PDFDocument.create();
            const loadedPDF = await PDFDocument.load(original);
            //get pages in original pdf
            const copiedPages = await modifiedPDF.copyPages(loadedPDF, loadedPDF.getPageIndices());

            // Add copied-pages to the modified-PDF, excluding specified pages (user enter them)
            for (let i = 0; i < copiedPages.length; i++) {
                if (excludePages.includes(i + 1)) continue;
                modifiedPDF.addPage(copiedPages[i]);
            }


            // Get the total number of pages in the modified PDF
            const pageCount = modifiedPDF.getPageCount();

            // Draw page numbers on each page
            for (let pageNum = 0; pageNum < pageCount; pageNum++) {
                const page = modifiedPDF.getPage(pageNum);
                const pageNumberText = `Page ${pageNum + 1}`;
                const { width, height } = page.getSize();
                const textSize = 10;
                const textWidth = font.widthOfTextAtSize(pageNumberText, textSize);
                const textHeight = font.heightAtSize(textSize);

                page.drawText(pageNumberText, {
                    x: (width - textWidth) / 2,
                    y: textHeight + 10, // Adjust the position as needed
                    size: textSize,
                    font: font,
                    color: rgb(0, 0, 0), // Black color
                });
            }

            // Check if the number of pages matches the number of tickets on the order
            // Skip check if delivery method is FlashSeat (4) or DirectTransfer (5) or (Mobile QR (3) with Exact market)
            let _unmatching = //!(3 == deliveryMethodRef.current && orderData.event_market == 'Exact') &&
                ![4, 5].some(x => x === deliveryMethodRef.current) && orderData.quantity !== pageCount;
            if (_unmatching) {
                notify('The number of pages must match the number of tickets on the order.', { type: 'error' });
            }
            setUploadBtn(prev => ({ ...prev, unmatching: _unmatching }));

            // Save the modified PDF and return as a blob
            const modifiedPdfBytes = await modifiedPDF.save();
            let blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });

            return blob;
        } catch (error) {
            console.error('Error merging and processing PDFs:', error);
            throw error; // Re-throw the error to be handled by the caller
        }
        //===========================================================
        function combineFile (mergedPdf: any, fileData: IData) {
            const { name, type, data, file } = fileData;
            if (type.includes('image')) {
                let canvas = document.createElement('canvas');
                let ctx = canvas.getContext('2d');
                // Create a new Image object
                let img = new Image();
                img.onload = function () {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);
                    // Decode QR code from the canvas
                    let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    let code = jsQR(imageData.data, imageData.width, imageData.height);
                    if (code) {
                        console.log('QR Code detected:', code.data);
                        arrQR.push(code.data);
                        setPreviewPDF(prev => ({ ...prev, isMobileQR: true }));
                        // You can do further processing here
                    } else {
                        console.log('No QR Code detected');
                    }
                    //extract text from canvas
                    extractText(imageData, ctx, canvas);
                };
                img.src = URL.createObjectURL(file);

                return addImageToPDF(data);
            } else {
                setUploadBtn(prev => ({ ...prev, hide: false }));
                if (type === 'application/pdf') {
                    return addPDFToPDF(data);
                } else {
                    return Promise.resolve();
                }
            }

            function extractText (imageData: any, ctx: any, canvas: any) {
                //Combine multiple text
                removeBackgroundText(imageData, ctx, canvas);
                Tesseract.recognize(file, 'eng').then(_ => {
                    if (_.data != null) {
                        let text = _.data.text;
                        //console.log(text);
                        arrText.push(text);
                    }
                }, e => console.log(e));
            }

            function removeBackgroundText (imageData: any, ctx: any, canvas: any) {
                // Remove background before extract text
                // Convert to grayscale
                // The image is converted to grayscale, which simplifies the data
                const idata = imageData.data;
                for (let i = 0; i < idata.length; i += 4) {
                    const r = idata[i];
                    const g = idata[i + 1];
                    const b = idata[i + 2];
                    const gray = 0.299 * r + 0.587 * g + 0.114 * b;
                    idata[i] = idata[i + 1] = idata[i + 2] = gray;
                }
                ctx.putImageData(imageData, 0, 0);

                // Apply thresholding
                // Pixels are converted to either black or white based on a threshold value, effectively removing the background
                const threshold = 128; // Adjust as needed
                for (let i = 0; i < idata.length; i += 4) {
                    const gray = idata[i];
                    const value = gray > threshold ? 255 : 0;
                    idata[i] = idata[i + 1] = idata[i + 2] = value;
                }
                ctx.putImageData(imageData, 0, 0);

                canvas.toBlob((blob: any) => {
                    Tesseract.recognize(
                        blob,
                        'eng'
                    ).then((_: any) => {
                        if (_.data != null) {
                            let text = _.data.text;
                            arrText.push(text);
                            verifyProofOfTransfer(arrText);
                        }
                    }, e => console.log(e))
                        .finally(_ => setUploadBtn(prev => ({ ...prev, hide: false })));;
                });
            }

            function addPDFToPDF (blob: any) {
                return new Promise<void>((resolve, reject) => {
                    PDFDocument.load(blob, { ignoreEncryption: true })
                        .then((pdfDoc: any) => {
                            if (pdfDoc.isEncrypted) {
                                if (previewPDF.excludes && previewPDF.newBlob) {
                                    return PDFDocument.load(previewPDF.newBlob).then((_: any) => mergedPdf.copyPages(_, _.getPageIndices()));
                                } else {
                                    return removeRestriction(file)
                                        .then((newBlob: any) => {
                                            setPreviewPDF(prev => ({ ...prev, newBlob }));
                                            return PDFDocument.load(newBlob).then((_: any) => mergedPdf.copyPages(_, _.getPageIndices()));
                                        });
                                }
                            }
                            return mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
                        })
                        .then((copiedPages: any) => {
                            copiedPages.forEach((page: any) => {
                                mergedPdf.addPage(page);
                            });
                            resolve();
                        })
                        .catch(reject);
                });

                function removeRestriction (blob: any) {
                    const formData = new FormData();
                    formData.append('file', blob, 'tickets.pdf');
                    return dataProvider.postData(controller, 'RemoveRestriction', formData).then((res: any) => res.data);
                }
            }

            function addImageToPDF () {
                return new Promise<void>((resolve, reject) => {
                    getImageEmbedMethod(type)
                        .then((embedMethod: any) => {
                            if (embedMethod) {
                                embedMethod(mergedPdf, data).then((image: any) => {
                                    const page = mergedPdf.addPage();

                                    const pageWidth = page.getWidth();
                                    const pageHeight = page.getHeight();
                                    const imageWidth = image.width;
                                    const imageHeight = image.height;

                                    const scaleWidth = pageWidth / imageWidth;
                                    const scaleHeight = pageHeight / imageHeight;
                                    const scale = Math.min(scaleWidth, scaleHeight);

                                    const scaledWidth = imageWidth * scale;
                                    const scaledHeight = imageHeight * scale;

                                    const x = (pageWidth - scaledWidth) / 2; // Center horizontally
                                    const y = (pageHeight - scaledHeight) / 2; // Center vertically

                                    page.drawImage(image, {
                                        x: x,
                                        y: y,
                                        width: scaledWidth,
                                        height: scaledHeight,
                                    });
                                    // Add a label with the image name at the bottom of the page
                                    page.drawText(removeAccents(name), {
                                        x: 10, y: 10, size: 10,
                                        font: font,
                                        color: rgb(0, 0, 0), // Black color
                                    });
                                    resolve();
                                }).catch(reject);
                            } else {
                                console.error('Unsupported image format:', type);
                            }
                        }).catch(reject);
                });
            }
        }

        function removeAccents (str: string) {
            return str
                .normalize('NFD')               // decompose accents
                .replace(/[\u0300-\u036f]/g, '') // remove accent marks
                .replace(/đ/g, 'd');             // replace đ with d
        }
    };
    //#endregion

    //#region verify text
    function verifyProofOfTransfer (arrText: string[]) {
        if (shippingPopup.isUploadPOA) return true;

        let orderNumber = window.currentOrder.order_number;
        let allowMarkets = ['axs'];
        if (arrText.length > 0 && allowMarkets.some(x => window.currentOrder.event_market == x)) {
            //Skipped validate when upload POA
            let textPOA = ['Your Transferred Tickets', 'Recipient Info'];
            if (textPOA.every(p => arrText.some(t => t.toLowerCase().includes(p.toLowerCase())))) {
                setShippingPopup(prev => ({ ...prev, isUploadPOA: true, btnUploadText: 'Upload POA' }));
                return true;
            }

            let customer = window.currentOrder.customer;
            if (customer) {
                //AXS: "You successfully transferred {QTY} tickets to {NAME} ({EMAIL}) for {EVENTNAME}" ...
                //Must matching: Event, Qty, Customer Name, Email ...
                let transferText = arrText.join('\n****\n').toLowerCase();//.replace(/\s*\n\s*/g, ' ').trim();
                let textSupport = ['you successfully transferred', 'transferred tickets'];
                console.log(transferText);
                if (textSupport.some(t => transferText.includes(t))) {
                    let eventDetailsText = `
                                            Event: ${window.currentOrder.event}
                                            QTY: ${window.currentOrder.quantity}
                                            Customer Name: ${customer.name}
                                            Customer Email: ${customer.email}`;
                    let result = compareEventDetails(transferText, eventDetailsText);
                    console.log(result);
                    //nameMatches, emailMatches, eventMatches, qtyMatches
                    let valid = result.qtyMatches, message = '';
                    if (!valid) {
                        message += ' *invaid QTY';
                    }
                    else {
                        valid = result.eventMatches;
                        if (!valid) message += ' *invaid EVENT';

                        valid = result.nameMatches || result.emailMatches;
                        if (!valid) message += ` *invaid ${!result.emailMatches ? 'CUS_EMAIL' : 'CUS_NAME'}`;
                    }
                    //Check same event | same name | email
                    if (!valid) {
                        notify(`Order #${orderNumber} - Unmatching proof of transfer: ` + message, { type: 'warning', autoHideDuration: 5000 });
                        setUploadBtn(prev => ({ ...prev, showForceUpload: true, invalidText: true }));
                        return false;
                    } else {
                        setUploadBtn(prev => ({ ...prev, showForceUpload: false, invalidText: false }));
                    }
                }
                else notify(`Order #${orderNumber} - Unsupported this format, skipped validate.`, { type: 'warning', autoHideDuration: 5000 });
            }
        }
        return true;

        //Comparison  for same event | cus name | email | exact QTY
        function compareEventDetails (transferText, eventDetailsText) {
            // Helper function to normalize and split text into words
            function splitWords (text) {
                if (typeof text !== 'string') {
                    return [];
                }
                return text.trim().toLowerCase()
                    // Replace special characters with spaces
                    .replace(/&/g, ' and ') // Replace '&' with 'and'
                    .replace(/[.,_]/g, ' ')  // Replace '.', ',', and '_' with spaces
                    .replace(/\band\b/g, ' ') // Replace the word 'and' with space
                    // Remove any other non-alphanumeric characters
                    .replace(/[^\w\s]/g, '')
                    .split(/\s+/);
            }

            function splitEmail (text) {
                if (typeof text !== 'string') {
                    return [];
                }
                return text.trim().toLowerCase()
                    // Replace special characters with spaces
                    .replace(/&/g, ' and ') // Replace '&' with 'and'
                    .replace(/[@]/g, ' ')  // Replace '@', ',', and '_' with spaces
                    // Remove any other non-alphanumeric characters
                    .replace(/[^\w\s]/g, '')
                    .split(/\s+/);
            }

            // Extract and normalize relevant words for event comparison
            function extractRelevantEventWords (eventText) {
                // Capture the event details text before any potential irrelevant segments
                let eventName = eventText.split(/\s-\s/)[0]; // Capture text up to the first '-'
                return splitWords(eventName);
            }

            // Extract details using regex
            let transferEventPattern = /for\s+(?<event>[\s\S]+?)(?:\s+at|\s+-|$)/i;
            let transferQtyPattern = /\s*(?<qty>\d+)(?:\s)?(?:t)?icket(?:s)?/i;
            //let transferNamePattern = /(?:t)?icket(?:s)?\s+to\s+(?<name>.+?)\s+\(/i;
            //let transferEmailPattern = /\((?<email>.+?)\)/i;
            let transferNameEmailRegex = /to\s+([\w\s]+)\s+\(?([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+(?:\s+\w+)?)\)?\s+for/;

            let eventPattern = /Event:\s(?<event>.+)/i;
            let qtyPattern = /QTY:\s(?<qty>\d+)/i;
            let namePattern = /Customer Name\s?:\s(?<name>.+)/i;
            let emailPattern = /Customer Email\s?:\s(?<email>.+)/i;

            // Extracting values from transferText
            //let transferNameMatch = transferText.match(transferNamePattern);
            //let transferEmailMatch = transferText.match(transferEmailPattern);
            const match = transferText.match(transferNameEmailRegex);
            let transferName = '', transferEmail = '';
            if (match) {
                transferName = match[1].trim(); // Captured name
                transferEmail = match[2];       // Captured email
            }

            let transferEventMatch = transferText.match(transferEventPattern);
            let transferQtyMatch = transferText.match(transferQtyPattern);

            // Extracting values from eventDetailsText
            let eventMatch = eventDetailsText.match(eventPattern);
            let qtyMatch = eventDetailsText.match(qtyPattern);
            let nameMatch = eventDetailsText.match(namePattern);
            let emailMatch = eventDetailsText.match(emailPattern);

            // Extract groups with traditional checks
            //let transferName = transferNameMatch ? transferNameMatch[1] : '';
            //let transferEmail = transferEmailMatch ? transferEmailMatch[1] : '';
            let transferEvent = transferEventMatch ? transferEventMatch[1] : '';
            let transferQty = transferQtyMatch ? transferQtyMatch[1] : '';

            let eventName = eventMatch ? eventMatch[1] : '';
            let eventQty = qtyMatch ? qtyMatch[1] : ''; // Directly use the quantity value as string
            let eventCustomerName = nameMatch ? nameMatch[1] : '';
            let eventCustomerEmail = emailMatch ? emailMatch[1] : '';

            // Split extracted values into words for comparison
            let transferNameWords = splitWords(transferName);
            let transferEmailWords = splitEmail(transferEmail);
            let transferEventWords = splitWords(transferEvent);

            let eventNameWords = splitWords(eventCustomerName);
            let eventEmailWords = splitEmail(eventCustomerEmail);
            let eventEventWords = extractRelevantEventWords(eventName);

            // Helper function to check if all elements of a smaller array are contained in a larger array
            function wordsContained (largerArray, smallerArray) {
                //let largerSet = new Set(largerArray);
                //return smallerArray.every(word => largerSet.has(word));
                return smallerArray.every(word => largerArray.some(x => x.includes(word)));
            }

            // Log the words for debugging
            console.log('Transfer Name Words: ', transferNameWords);
            console.log('Event Name Words: ', eventNameWords);
            console.log('Transfer Email Words: ', transferEmailWords);
            console.log('Event Email Words: ', eventEmailWords);
            console.log('Transfer Event Words: ', transferEventWords);
            console.log('Event Event Words: ', eventEventWords);
            console.log('Transfer Quantity: ', transferQty);
            console.log('Event Quantity: ', eventQty);

            // Perform comparisons
            let nameMatches = wordsContained(eventNameWords, transferNameWords);
            let emailMatches = wordsContained(eventEmailWords, transferEmailWords);
            let eventMatches = wordsContained(eventEventWords, transferEventWords);
            let qtyMatches = transferQty === eventQty; // Direct comparison for quantity

            return {
                nameMatches,
                emailMatches,
                eventMatches,
                qtyMatches
            };
        }
    }
    //#endregion

    //#region Transfer Complete functionality
    /**
     * Determines if the Transfer Complete button should be shown based on order criteria.
     * Button is shown for orders from supported markets (AXS, Exact) with Direct Transfer (5)
     * or Flash (4) delivery methods and when the order status allows completion.
     * @returns {boolean} True if button should be shown
     */
    function shouldShowTransferCompleteBtn () {
        if (!orderData) return false;
        
        // Must be from a supported market (case-insensitive)
        if (!TRANSFER_COMPLETE_MARKETS.includes(orderData.event_market?.toLowerCase())) return false;
        
        // Must be Direct Transfer (5) or Flash (4) delivery method
        const validDeliveryMethods = [4, 5]; // FlashSeat: 4, DirectTransfer: 5
        if (!validDeliveryMethods.includes(orderData.delivery_method)) return false;
        
        // Must be in a status that allows completion
        const allowedStatuses = [STATUS_ENUM.Unprocessed, STATUS_ENUM.Processing, STATUS_ENUM.PendingShipment];
        return allowedStatuses.includes(parseInt(orderData.internal_status));
    }

    /**
     * Handles Transfer Complete button click for supported markets.
     * Checks for existing TFD notes to prevent duplicates, then adds a note if none exists
     * and changes status to complete, then triggers save.
     */
    const handleTransferComplete = () => {
        if (checkValidTransferLink())
        {
            notify('Switching to Upload button to process the provided transfer links.', { type: 'info', autoHideDuration: 3000 });
            // Switch to upload button
            return uploadShipping();
        }

        if (!shouldShowTransferCompleteBtn()) {
            notify('Transfer Complete is not available for this order', { type: 'warning' });
            return;
        }

        // Start loading
        setTransferLoading(true);

        // Get customer email from order data
        const customerEmail = customer.email;
        if (!customerEmail) {
            notify('Customer email not found. Cannot complete transfer.', { type: 'error' });
            setTransferLoading(false);
            return;
        }

        // Validate email format
        const emailValidationPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!emailValidationPattern.test(customerEmail)) {
            notify('Invalid customer email format found.', { type: 'error' });
            setTransferLoading(false);
            return;
        }

        // Check for existing TFD note before adding a new one
        const noteText = `TFD ${customerEmail}`;
        
        // Helper function to check if a note is a transfer note (case-insensitive)
        const isTransferNote = (note: IData) => {
            if (!note.post) return false;
            const postLower = note.post.toLowerCase();
            return postLower.includes('tfd ') || postLower.includes('transferred to ');
        };
        
        // Helper function to update status and save
        const completeOrder = () => {
            let finalOrder = { ...orderData, internal_status: STATUS_ENUM.Completed };
            setOrderData(finalOrder);
            saveOrder(undefined, finalOrder);
            setTransferLoading(false);
            closeShippingPopup();
        };
        
        // Check if TFD note already exists in order notes
        // Supports formats: "TFD @email" or "transferred to email"
        const existingTfdNote = orderData.order_notes?.find(isTransferNote) ||
                               orderData.order_fullnotes?.find(isTransferNote);

        if (existingTfdNote) {
            notify('Transfer note already exists for this order. Proceeding with status update only.', {
                type: 'info',
                autoHideDuration: 3000
            });

            // Update status to completed and trigger save without adding duplicate note
            completeOrder();
            return;
        }

        // No existing TFD note found, proceed to add new note
        addTfdNote(noteText);

        // Helper function to add TFD note
        function addTfdNote (noteText: string) {
            dataProvider
                .postData(vendor.controller, 'Note', {
                    order_id: orderData.id,
                    order_number: orderData.order_number,
                    post: noteText
                })
                .then((rs: any) => {
                    if (rs.data) {
                        notify('Transfer Complete note added successfully.', { type: 'success', autoHideDuration: 2000 });

                        // Update status to completed and trigger save
                        completeOrder();
                    } else {
                        notify('Failed to add Transfer Complete note.', { type: 'error', autoHideDuration: 3000 });
                        setTransferLoading(false);
                    }
                })
                .catch((error: any) => {
                    notify(`Transfer Complete note failed: ${error?.message || error}`, { type: 'error', autoHideDuration: 3000 });
                    setTransferLoading(false);
                });
        }
    };
    //#endregion

    // Render mobile QR section
    const showMobileQR = deliveryMethodRef.current == 3 || previewPDF.isMobileQR;
    const handleExcludes = (props: IData) => {
        setPreviewPDF(prev => ({ ...prev, excludes: props.target.value }));
    };
    const handleKeyUpExclude = async (props: IData) => {
        if (props.key == 'Enter') {
            await workWithFiles(previewPDF.selectedFiles);
        }
    };
    const deleteTransferLink = (props: IData) => {
        let index = props.currentTarget.id.split('-')[1] / 1;
        setTransferLink(prev => prev.filter(x => prev.indexOf(x) != index));//id with index always reset after render, so this condition is true
    };

    // Check valid transfer link -> switch upload button
    const checkValidTransferLink = () => {
        const links = transferLink.map(x => x.url).filter(x => x);
        return links.length > 0;
    };

    // Upload button
    const uploadShipping = async () => {
        const file = fileInputRef.current;
        const links = transferLink.map(x => x.url).filter(x => x);
        let orderNumber = orderData.order_number;
        let isUploadUrl = (shippingPopup.isUploadUrl || shippingPopup.isBothUpload) && links.length > 0;

        if (!validateUpload()) return;

        if (shippingPopup.isUploadPOA) {
            if (!file) {
                notify('Please select a file first');
                return;
            }
            const formData = new FormData();
            formData.append('file', file);
            formData.append('orderNumber', orderNumber);

            if (previewPDF.blobURL) {
                await dataProvider.postData(controller, 'UploadProofOfAcceptance', formData)
                    .then(response => {
                        if (response?.data) {
                            notify(`${response.data.message}`, { type: response.data.success ? 'success' : 'error', autoHideDuration: 2000 });
                            setDlgState(prev => ({ ...prev, reload_order: !prev.reload_order }));
                        } else {
                            notify(`ERROR: ${response?.message}`, { type: 'error', autoHideDuration: 2000 });
                        }
                    })
                    .catch(err => notify(`Upload failed: ${err?.message}`, { type: 'error', autoHideDuration: 3000 }))
                    .finally(_ => closePopup());
            }
        }
        else if (isUploadUrl) {
            await dataProvider.postData(controller, `CompleteUrl?orderNumber=${orderNumber}`, links)
                .then(response => {
                    if (response?.data) {
                        notify(`${response.data.message}`, { type: response.data.success ? 'success' : 'error', autoHideDuration: 3000 });
                        // internal_status update handled in backend: updated to completed
                        setDlgState(prev => ({ ...prev, reload_order: !prev.reload_order }));
                    } else {
                        notify(`ERROR: ${response?.message}`, { type: 'error', autoHideDuration: 3000 });
                    }
                })
                .catch(err => notify(`ERROR: ${err?.message}`, { type: 'error', autoHideDuration: 3000 }))
                .finally(_ => closePopup());
        }
        else {
            if (!file) {
                notify('Please select a file first');
                return;
            }
            const formData = new FormData();
            formData.append('file', file);
            formData.append('orderNumber', orderNumber);
            formData.append('mobileQR', previewPDF?.isMobileQR);
            if (uploadBtn.forceUpload) {
                notify(`Order #${orderNumber} - you are skipping validate proof of transfer.`, { type: 'warning', autoHideDuration: 3000 });
                formData.append('forceUpload', uploadBtn.forceUpload);
            }

            await dataProvider.postData(controller, 'CompleteETickets', formData)
                .then(response => {
                    if (response?.data) {
                        notify(`${response.data.message}`, { type: response.data.success ? 'success' : 'error', autoHideDuration: 3000 });
                        // internal_status update handled in backend: updated to completed
                        setDlgState(prev => ({ ...prev, reload_order: !prev.reload_order }));
                    } else {
                        notify(`ERROR: ${response?.message}`, { type: 'error', autoHideDuration: 3000 });
                    }
                })
                .catch(err => notify(`Upload failed: ${err?.message}`, { type: 'error', autoHideDuration: 3000 }))
                .finally(_ => closePopup());
        }

        function validateUpload () {
            if (shippingPopup.isBothUpload) {
                if (links.length == 0 && !file) {
                    notify('Please upload URLs or Proofs', { type: 'warning', autoHideDuration: 2200 });
                    return false;
                }
            } else if (shippingPopup.isUploadUrl) {
                if (links.length == 0) {
                    notify('Please upload URLs', { type: 'warning', autoHideDuration: 2200 });
                    return false;
                }
            } else if (!file) {
                notify('Please upload Files', { type: 'warning', autoHideDuration: 2200 });
                return false;
            }
            return true;
        }
    };
    /**
     * Handles the shipping button click event for order validation and customer info reveal.
     * This function triggers shipping validation through the backend and reveals customer information
     * for Direct Transfer (delivery_method = 4) and Flash orders (delivery_method = 5) after successful validation.
     * For GoTickets and TicketNetwork vendors, this triggers email recapture functionality in the backend.
     *
     * @async
     * @function clickShippingButton
     * @returns {Promise<void>} Promise that resolves when shipping validation and customer reveal is complete
     */
    async function clickShippingButton () {
        await dataProvider.postData(controller, 'ValidateShipping', orderData)
            .then(response => {
                if (response.data) {
                    //Temporarily: caused by login failed on VS portal, need to show warning instead of error and allow to open shipping popup
                    if (response.data.message && !response.data.message.toLowerCase().includes('skipped sync order')) {
                        notify(`ERROR: ${response.data.message}`, { type: 'warning', autoHideDuration: 3000 });
                        if (response.data.message.includes('Re-synced customer')) {
                            setDlgState(prev => ({ ...prev, reload_order: !prev.reload_order }));
                        }
                    } else {
                        let isShowShipping = !validate('shipping');
                        if (isShowShipping) {
                            let internal_status = orderData.internal_status;
                            if (parseInt(internal_status) === STATUS_ENUM.Completed) {
                                if (!validComplete()) {
                                    return;
                                }
                            }
                            setShippingPopup(prev => ({ ...prev, open: true }));
                        }
                        // Reveal customer info for Direct Transfer and Flash orders after successful shipping validation
                        const isDirectTransferOrFlash = orderData.delivery_method === 4 || orderData.delivery_method === 5;
                        if (isDirectTransferOrFlash && customer && revealCustomerInfo) {
                            revealCustomerInfo(customer);
                        }
                    }
                } else {
                    notify(`ERROR: ${response?.message}`, { type: 'error', autoHideDuration: 3000 });
                }

            })
            .catch(err => notify(`Validated shipping Error: ${err?.message}`, { type: 'error', autoHideDuration: 3000 }));
    }
    const disabledUploadButton = () => {
        if (!uploadBtn.showForceUpload) {
            return uploadBtn?.unmatching || uploadBtn?.invalidText || false;
        } else return !uploadBtn.forceUpload;
    };

    const gridUploadUrl = (label?: string) => {
        return (
            <Grid container sx={{ display: 'block' }}>
                <Grid size={12} sx={{ marginBottom: '7px' }}>
                    <b>{label ?? 'Transfer links'}</b>
                </Grid>
                <>
                    {transferLink.map((tl, index) => {
                        return (
                            <Grid container spacing={1} sx={{ marginBottom: '5px' }} key={`${orderData.order_number}-trlink-${index}`}>
                                <Grid size={10}>
                                    <TextField value={tl.url} variant="outlined" margin="none" onChange={(props) => setTransferLink(prev => prev.map(obj =>
                                        prev.indexOf(obj) == transferLink.indexOf(tl) ? { ...obj, url: props.target.value } : obj
                                    ))} />
                                </Grid>
                                <Grid size={1} >
                                    {index == 0 && <Button variant="outlined" size="small" onClick={() => setTransferLink(prev => ([...prev, { url: '' }]))}>Add</Button>}
                                    {index > 1 && <IconButton id={`rmbtn-${index}`} onClick={deleteTransferLink} ><RemoveCircleOutline /></IconButton>}
                                </Grid>
                            </Grid>
                        );
                    })}
                </>
            </Grid>
        );
    };

    const gridUploadFile = () => {
        return (
            <Grid container sx={{ marginBottom: '10px' }}>
                <Grid size={12}>
                    <b>Upload tickets, proof of transfer screenshots (acceptable: pdf, image)</b>
                </Grid>
                <Grid size={6} sx={{ marginTop: '7px', paddingRight: '5px', '&:hover': { cursor: 'pointer' } }}>
                    {/* upload pdf or img */}
                    <div {...getRootProps()} >
                        <input {...getInputProps()} />
                        {
                            <Stack direction='row' style={{ border: 'solid 0.5px gray', borderRadius: '4px', justifyContent: 'center' }}>
                                <UploadFile sx={{ marginTop: '6px' }} />
                                <p style={{ margin: '7px' }}>
                                    {previewPDF.filesNum ? `Selected file(s): ${previewPDF.selectedFiles.map(f => f.name).join('; ')} (${previewPDF.filesNum})` : 'Click to select files'}
                                </p>
                            </Stack>
                        }
                    </div>
                </Grid>
                <Grid size={4}>
                    <TextField placeholder="excludes: e.g. 1-5, 8, 11-13" variant="outlined" size="small" margin="none" sx={{ marginTop: '7px' }}
                        value={previewPDF.excludes || ''}
                        onChange={handleExcludes}
                        onKeyUp={handleKeyUpExclude}
                    />
                </Grid>
                <Grid size={2} sx={{ paddingLeft: '5px' }}>
                    {
                        showMobileQR && <MCheckbox defaultValue={previewPDF.isMobileQR} sx={{ fontSize: '12px' }}
                            onChange={(value) => setPreviewPDF(prev => ({ ...prev, isMobileQR: value }))} label="Mobile QRs" />
                    }
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            {shippingPopup.showShippingBtn && <LoadingButton onClick={clickShippingButton} variant="contained" size="small"
                sx={{ textTransform: 'capitalize', backgroundColor: '#5cc0de', '&:hover': { backgroundColor: '#1791d3' } }}
                startIcon={<MultipleStop />} children={isUploadPOA ? 'Upload Proof' : 'Shipping'} />}
            <MDialog
                title={`#${orderData.order_number} - ${shippingPopup.title}`}
                maxWidth={'md'}
                open={shippingPopup.open}
                handleClose={closeShippingPopup}
                name={'order-shipping-dialog'}
                action={
                    <Stack direction="row" spacing={1}>
                        {uploadBtn.showForceUpload && <MCheckbox id='cb_force_upload' onChange={(value) => setUploadBtn(prev => ({ ...prev, forceUpload: value }))} label="force upload" />}
                        {!uploadBtn.hide && <LoadingButton startIcon={<Upload />} children={shippingPopup?.btnUploadText || 'Upload'} variant="contained" size="small" color="primary"
                            onClick={uploadShipping} disabled={disabledUploadButton()} />}
                        {!previewPDF.filesNum && shouldShowTransferCompleteBtn() &&
                            <LoadingButton
                                onClick={handleTransferComplete}
                                variant="contained"
                                size="small"
                                loading={transferLoading}
                                disabled={transferLoading}
                                sx={{
                                    textTransform: 'capitalize',
                                    backgroundColor: '#4CAF50',
                                    '&:hover': { backgroundColor: '#45a049' }
                                }}
                            >
                                {`${orderData.event_market?.toUpperCase()} Transfer Complete`}
                            </LoadingButton>
                        }
                    </Stack>
                }
                className={'order-dialog'}
                disabled={previewPDF.loadingFile}
                children={
                    <Grid container spacing={1}>
                        <Grid size={8}>
                            <Grid container spacing={2}>
                                <Grid size={3} sx={{ textAlign: 'right' }}>
                                    <b>Event</b>
                                </Grid>
                                <Grid size={9}>
                                    <span>{orderData?.event}</span>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid size={3} sx={{ textAlign: 'right' }}>
                                    <b>Venue</b>
                                </Grid>
                                <Grid size={8} >
                                    <span>{orderData?.venue}</span>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid size={3} sx={{ textAlign: 'right' }}>
                                    <b>Date</b>
                                </Grid>
                                <Grid size={9}>
                                    <span>{formatDateTime(orderData?.date, 'MMM DD YYYY hh:mm A')}</span>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={4}>
                            <Grid container spacing={2}>
                                <Grid size={3} sx={{ textAlign: 'right' }}>
                                    <b>Section</b>
                                </Grid>
                                <Grid size={9}>
                                    <span>{orderData?.section}</span>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid size={3} sx={{ textAlign: 'right' }}>
                                    <b>Row</b>
                                </Grid>
                                <Grid size={8} >
                                    <span>{orderData?.row}</span>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid size={3} sx={{ textAlign: 'right' }}>
                                    <b>Seat</b>
                                </Grid>
                                <Grid size={9}>
                                    <span>{orderData?.lo_seat ? `${orderData.lo_seat} - ${orderData.hi_seat}` : orderData?.seat}</span>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid size={3} sx={{ textAlign: 'right' }}>
                                    <b>Qty</b>
                                </Grid>
                                <Grid size={9}>
                                    <span>{orderData?.quantity}</span>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={8}>
                            {orderData?.customer?.name.trim() && <Grid container spacing={2}>
                                <Grid size={3} sx={{ textAlign: 'right' }}>
                                    <b>Customer Name</b>
                                </Grid>
                                <Grid size={9}>
                                    <span>{`${orderData?.customer?.name}`}</span>
                                </Grid>
                            </Grid>}
                            {orderData?.customer?.email.trim() && <Grid container spacing={2}>
                                <Grid size={3} sx={{ textAlign: 'right' }}>
                                    <b>Customer Email</b>
                                </Grid>
                                <Grid size={8} >
                                    <span>{orderData?.customer?.email}</span>
                                </Grid>
                            </Grid>}
                        </Grid>
                        <Grid size={12}>
                            <Divider />
                            <Grid container spacing={1} sx={{ marginTop: '10px', display: 'block' }}>
                                {shippingPopup.isBothUpload && gridUploadFile()}
                                {shippingPopup.isBothUpload && gridUploadUrl('Transfer via SMP (https://securemypass.com*)')}
                                {/* else not both-upload                                 */}
                                {!shippingPopup.isBothUpload && shippingPopup.isUploadUrl && gridUploadUrl()}
                                {!shippingPopup.isBothUpload && !shippingPopup.isUploadUrl && gridUploadFile()}
                            </Grid>
                        </Grid>
                        {previewPDF.blobURL && <Grid size={12}>
                            <embed src={previewPDF.blobURL} type={'application/pdf'} width={previewPDF.width} height={previewPDF.height}></embed>
                        </Grid>
                        }
                    </Grid>
                }
            >
            </MDialog>
        </>
    );
};
export default OrderShipping;
